import * as React from "react";
import { useQueries } from "@tanstack/react-query";
import { StatusCard } from "@/components/dashboard/StatusCard";
import { QueryStateBoundary } from "@/components/dashboard/QueryStateBoundary";
import { ReportMetadataTable, type ReportMetadataRow } from "../ReportMetadataTable";
import { useAvailableReports } from "@/hooks/queries/useReports";
import { useCertificationReport } from "@/hooks/queries/useCertificationReport";
import { reportingService } from "@/services/reportingService";
import { appConfig } from "@/utils/config";

interface ReportSummaryPayload {
  report_id: string;
  report_type: string;
  generated_at: string;
}

interface ReportMetadataPayload {
  report_id: string;
  generated_at: string;
}

interface CertificationReportPayload {
  generated_at: string;
  overall_passed: boolean;
}

/**
 * Backend v2.1 (Sprint 4.3) `GET /api/v1/reports/{report_name}` - reads
 * each already-listed report type's own metadata (one call per type,
 * mirroring `WatchlistSection`'s existing `useQueries` precedent for
 * per-item real reads). `version`/`source` remain "Not reported by
 * backend": `ReportMetadataDto` carries no such fields.
 */
function ReportMetadataRows({ reports }: { reports: ReportSummaryPayload[] }): React.JSX.Element {
  const metadataQueries = useQueries({
    queries: reports.map((report) => ({
      queryKey: ["reports", "metadata", appConfig.defaultPortfolioId, report.report_type],
      queryFn: () => reportingService.getReportMetadata(appConfig.defaultPortfolioId, report.report_type),
    })),
  });

  const certificationQuery = useCertificationReport();

  const rows: ReportMetadataRow[] = reports.map((report, index) => {
    const metadata = metadataQueries[index].data as unknown as ReportMetadataPayload | undefined;
    return {
      report_name: report.report_type,
      generated_time: metadata?.generated_at ?? report.generated_at,
      version: null,
      source: null,
      status: "generated",
    };
  });

  return (
    <QueryStateBoundary query={certificationQuery}>
      {(payload) => {
        const certification = payload as unknown as CertificationReportPayload;
        return (
          <ReportMetadataTable
            reports={[
              ...rows,
              {
                report_name: "Health Report",
                generated_time: certification.generated_at,
                version: null,
                source: "Platform Certification",
                status: certification.overall_passed ? "generated" : "degraded",
              },
            ]}
          />
        );
      }}
    </QueryStateBoundary>
  );
}

export function ReportMetadataSection(): React.JSX.Element {
  const reportsQuery = useAvailableReports(appConfig.defaultPortfolioId);

  return (
    <StatusCard title="Report Metadata">
      <QueryStateBoundary query={reportsQuery}>
        {(payload) => <ReportMetadataRows reports={payload as unknown as ReportSummaryPayload[]} />}
      </QueryStateBoundary>
    </StatusCard>
  );
}
